import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { MessageCircle, X, Send, Loader2, Sparkles, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const SupportChat = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Aria, your Assagart support assistant. How can I help you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = newMessages.slice(-10).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await api.supportChat({
        message: userMessage,
        conversation_history: conversationHistory
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        suggestedActions: response.data.suggested_actions || []
      }]);
    } catch (error) {
      console.error('Support chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I couldn't process your request. Please try again or contact support@assagart.com."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAction = (action) => {
    if (action.action === 'navigate' && action.path) {
      navigate(action.path);
      setIsOpen(false);
    }
  };

  // Quick questions for new conversations
  const quickQuestions = [
    "How do I add an asset?",
    "What's included in Professional?",
    "How does AI valuation work?",
    "How do I upgrade my plan?"
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
    setTimeout(() => handleSend(), 100);
  };

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 bg-gold text-black rounded-full shadow-lg flex items-center justify-center hover:bg-gold/90 transition-all md:bottom-6 ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="support-chat-btn"
      >
        <MessageCircle size={24} />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[500px] max-h-[80vh] bg-[#0A0A0A] border border-[#1A1A1A] shadow-2xl flex flex-col overflow-hidden"
          data-testid="support-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#1A1A1A] bg-[#050505]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                <Sparkles size={20} className="text-gold" />
              </div>
              <div>
                <p className="text-white font-medium">Aria</p>
                <p className="text-xs text-emerald-400">Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-muted-foreground hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 ${
                    msg.role === 'user'
                      ? 'bg-gold text-black rounded-tl-lg rounded-tr-lg rounded-bl-lg'
                      : 'bg-[#1A1A1A] text-white rounded-tl-lg rounded-tr-lg rounded-br-lg'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <div className="text-sm chat-markdown">
                      <ReactMarkdown
                        components={{
                          p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                          strong: ({children}) => <strong className="font-semibold text-gold">{children}</strong>,
                          em: ({children}) => <em className="italic">{children}</em>,
                          ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-2 ml-1">{children}</ul>,
                          ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-2 ml-1">{children}</ol>,
                          li: ({children}) => <li className="text-sm">{children}</li>,
                          code: ({children}) => <code className="bg-[#0A0A0A] text-gold px-1 py-0.5 rounded text-xs">{children}</code>,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  
                  {/* Suggested Actions */}
                  {msg.suggestedActions && msg.suggestedActions.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {msg.suggestedActions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={() => handleAction(action)}
                          className="flex items-center gap-2 text-xs text-gold hover:text-gold/80 transition-colors"
                        >
                          <ChevronRight size={14} />
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] p-3 rounded-tl-lg rounded-tr-lg rounded-br-lg">
                  <div className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin text-gold" />
                    <span className="text-sm text-muted-foreground">Aria is typing...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions (show only at start) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuickQuestion(q)}
                    className="text-xs px-3 py-1.5 bg-[#1A1A1A] border border-[#262626] text-muted-foreground hover:text-white hover:border-gold/30 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-[#1A1A1A]">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                disabled={loading}
                className="flex-1 bg-[#1A1A1A] border border-[#262626] text-white text-sm px-4 py-2 focus:outline-none focus:border-gold/50 disabled:opacity-50"
                data-testid="support-chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="btn-primary px-4"
                data-testid="support-chat-send"
              >
                <Send size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChat;
